import {
  ExternalIngestionDocumentFragment,
  GetIngestionDocumentFragment,
  IngestionDocumentValidationErrorReviewFragment,
  RuleType,
  VariableSource,
} from '@client/graphql/__generated__/types';
import { MultiFieldValue } from '@common/graphql';
import { getPageNumbers } from '@common/utils/validation';
import { TITLE_TERMINATORS, titleCase } from 'title-case';
import type {
  ComplianceIngestionDocumentValidation,
  ComplianceValidationReportData,
} from '../Compliance.types';
import { getFieldLevelComparisonData } from './fieldComparison.utils';
import { createFieldKeyToLabelMapping } from './fieldMapping.utils';

export interface ValidationItem {
  message: string;
  ruleUuid: string;
  ruleType?: RuleType;
  pageNumbers?: number[];
  contractDocumentUuid?: string;
  mappingKeys?: string[];
  ingestionDocumentValidationId?: string;
  ruleFormula?: string;
  isDocumentValidation?: boolean;
  isPipelineValidation?: boolean;
  isOrganizationRule?: boolean;
  isOrganizationIgnored?: boolean;
  inputValues?: {
    variableSource?: VariableSource;
    fieldKey: string;
    value: string;
  }[];
}

export interface ValidationResult {
  ruleUuid: string;
  output: boolean;
  message: string;
  ruleType?: RuleType;
  /** @deprecated legacy single-page; read via getPageNumbers(). */
  pageNumber?: number;
  pageNumbers?: number[];
  contractDocumentUuid?: string;
  fieldMappingKey?: string;
  focusFieldMappingKeys?: string[] | null;
  ruleFormula?: string;
  isDocumentValidation?: boolean;
  isPipelineValidation?: boolean;
  isOrganizationRule?: boolean;
  isOrganizationIgnored?: boolean;
  inputValues?: {
    variableSource?: VariableSource;
    fieldKey: string;
    value: string;
  }[];
  documentVersionFieldUuid?: string;
}

export interface ValidationResultsForDocument {
  errors: ValidationItem[];
  successes: ValidationItem[];
  organizationIgnoredRules?: ValidationItem[];
}

/**
 * The lowest page a validation item is attributed to, used as its primary
 * sort key. Items with no page attribution sort last (treated as +Infinity).
 */
function getFirstPageNumber(item: ValidationItem): number {
  return item.pageNumbers?.length
    ? Math.min(...item.pageNumbers)
    : Number.POSITIVE_INFINITY;
}

interface FieldPosition {
  pageNumber: number;
  /** Normalized, top-origin vertical offset — smaller is higher on the page. */
  y: number;
}

/** `contractDocumentUuid` → `mappingKey` → field positions on each page. */
type FieldPositionLookup = Map<string, Map<string, FieldPosition[]>>;

/**
 * Builds a lookup of field geometry from the repositioned `fields` map, used
 * to order same-page items top-to-bottom.
 *
 * The geometry is only present on the GetIngestionDocument fragment (the side
 * panel / extension); other fragments (e.g. the overview's External document)
 * omit it. When absent we return an empty lookup — the within-page tiebreak
 * becomes a no-op and items keep page-level ordering. We never fetch geometry
 * that isn't already on the document.
 */
function buildFieldPositionLookup(
  document: GetIngestionDocumentFragment | ExternalIngestionDocumentFragment
): FieldPositionLookup {
  const lookup: FieldPositionLookup = new Map();
  const fields =
    'fields' in document
      ? (document.fields as Record<string, unknown> | null | undefined)
      : undefined;

  if (!fields) return lookup;

  for (const [contractDocumentUuid, rawFields] of Object.entries(fields)) {
    if (!Array.isArray(rawFields)) continue;

    const positionsByMappingKey = new Map<string, FieldPosition[]>();
    for (const rawField of rawFields as Array<{
      mappingKey?: unknown;
      pageNumber?: unknown;
      y?: unknown;
    }>) {
      const { mappingKey, pageNumber, y } = rawField;
      if (
        typeof mappingKey !== 'string' ||
        typeof pageNumber !== 'number' ||
        typeof y !== 'number'
      ) {
        continue;
      }

      const positions = positionsByMappingKey.get(mappingKey) ?? [];
      positions.push({ pageNumber, y });
      positionsByMappingKey.set(mappingKey, positions);
    }

    lookup.set(contractDocumentUuid, positionsByMappingKey);
  }

  return lookup;
}

/**
 * Vertical position of an item's topmost field on `page`, used to order items
 * within a single page (smaller = higher = first).
 *
 * Returns +Infinity ("sort last within the page") for items we can't place:
 * multi-page items, document-level items with no field, and items whose fields
 * aren't in the lookup (e.g. fragments without geometry). Best-effort by
 * design — it only refines ordering where the position already exists.
 */
function getWithinPageSortValue(
  item: ValidationItem,
  page: number,
  lookup: FieldPositionLookup
): number {
  if (item.pageNumbers?.length !== 1) return Number.POSITIVE_INFINITY;
  if (!item.contractDocumentUuid || !item.mappingKeys?.length) {
    return Number.POSITIVE_INFINITY;
  }

  const positionsByMappingKey = lookup.get(item.contractDocumentUuid);
  if (!positionsByMappingKey) return Number.POSITIVE_INFINITY;

  let topY = Number.POSITIVE_INFINITY;
  for (const mappingKey of item.mappingKeys) {
    const positions = positionsByMappingKey.get(mappingKey);
    if (!positions) continue;

    for (const position of positions) {
      if (position.pageNumber === page && position.y < topY) {
        topY = position.y;
      }
    }
  }

  return topY;
}

/**
 * Orders validation items by their first failing page (ascending) so the list
 * reads top-to-bottom through the document. Within a page, items are ordered
 * by their topmost field's vertical position when geometry is available;
 * multi-page and position-less items fall last on that page. Stable: items
 * that tie on both keys keep their original relative order.
 */
function sortValidationItemsByPage(
  items: ValidationItem[],
  fieldPositionLookup: FieldPositionLookup
): ValidationItem[] {
  return [...items].sort((a, b) => {
    const aPage = getFirstPageNumber(a);
    const bPage = getFirstPageNumber(b);
    if (aPage !== bPage) return aPage - bPage;

    const aY = getWithinPageSortValue(a, aPage, fieldPositionLookup);
    const bY = getWithinPageSortValue(b, bPage, fieldPositionLookup);
    if (aY === bY) return 0;

    return aY - bY;
  });
}

/**
 * Extracts validation results for a specific document.
 *
 * @param document - The ingestion document to get validation results for
 * @param validationData - Optional validation data containing document validations
 * @param contractDocumentId - Optional specific contract document ID to filter results
 * @param hiddenDocumentValidationReviews - Optional review records marking
 *   errors as hidden. Any error matching a hidden review (same `ruleUuid`,
 *   `contractDocumentUuid`, and `ingestionDocumentValidation.id`) is removed
 *   from the returned `errors`, mirroring
 *   {@link getErrorAndSuccessCountsForContractDocument}.
 * @returns Object containing arrays of error, success, and org-ignored validation items
 *
 * @example
 * const { errors, successes } = getValidationResultsForDocument(
 *   document,
 *   validationData,
 *   'contract-123'
 * );
 * Log.info(`Found ${errors.length} errors and ${successes.length} successes`);
 */
export function getValidationResultsForDocument(
  document: GetIngestionDocumentFragment | ExternalIngestionDocumentFragment,
  validationData?: ComplianceValidationReportData,
  contractDocumentId?: string,
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[]
): ValidationResultsForDocument {
  if (!document || !validationData?.documentValidations) {
    return { errors: [], successes: [], organizationIgnoredRules: [] };
  }

  // Get contract document UUIDs from the document
  const contractDocumentUuids = new Set(
    document.contractDocuments?.map((cd: { id: string }) => cd.id) || []
  );

  if (contractDocumentUuids.size === 0) {
    return { errors: [], successes: [], organizationIgnoredRules: [] };
  }

  const errors: ValidationItem[] = [];
  const successes: ValidationItem[] = [];
  const organizationIgnoredRules: ValidationItem[] = [];
  const errorIds = new Set<string>();
  const successIds = new Set<string>();
  const organizationIgnoredRuleIds = new Set<string>();

  // Process each document validation
  validationData.documentValidations.forEach(
    (docValidation: ComplianceIngestionDocumentValidation) => {
      // Handle the dynamic Record structure from GraphQL
      const aiValidationResults =
        docValidation.aiValidationResults as unknown as
          | ValidationResult[]
          | undefined;
      const validationResults = docValidation.validationResults as unknown as
        | ValidationResult[]
        | undefined;

      // Prioritize AI validation results, but fall back to regular validation results if AI results are empty
      const resultsToProcess =
        aiValidationResults && aiValidationResults.length > 0
          ? aiValidationResults
          : validationResults;

      if (resultsToProcess) {
        resultsToProcess.forEach((result) => {
          // Only include results for this document's contract documents
          if (
            result.contractDocumentUuid &&
            contractDocumentUuids.has(result.contractDocumentUuid)
          ) {
            // If contractDocumentId is provided, filter by specific contract document
            if (
              contractDocumentId &&
              result.contractDocumentUuid !== contractDocumentId
            ) {
              return;
            }

            // Use focusFieldMappingKeys if present, otherwise fall back to inputValues
            const mappingKeys =
              result.focusFieldMappingKeys ??
              result.inputValues?.reduce((acc, val) => {
                if (val.variableSource === VariableSource.FIELD) {
                  acc.push(val.fieldKey);
                }

                return acc;
              }, [] as string[]) ??
              (result.fieldMappingKey ? [result.fieldMappingKey] : []);

            // Ensure inputValues have properly serialized values
            const normalizedInputValues = result.inputValues?.map((input) => {
              let normalizedValue: string;
              if (typeof input.value === 'string') {
                normalizedValue = input.value;
              } else if (
                typeof input.value === 'object' &&
                input.value !== null
              ) {
                // GraphQL sometimes returns objects instead of JSON strings
                normalizedValue = JSON.stringify(input.value);
              } else {
                normalizedValue = String(input.value);
              }

              return {
                ...input,
                value: normalizedValue,
              };
            });

            const item: ValidationItem = {
              message: result.message,
              ruleUuid: result.ruleUuid,
              ruleType: result.ruleType,
              pageNumbers: getPageNumbers(result),
              contractDocumentUuid: result.contractDocumentUuid,
              mappingKeys,
              inputValues: normalizedInputValues,
              ingestionDocumentValidationId: docValidation.id,
              ruleFormula: result.ruleFormula,
              isDocumentValidation: result.isDocumentValidation,
              isPipelineValidation: result.isPipelineValidation,
              isOrganizationRule: result.isOrganizationRule,
              isOrganizationIgnored: result.isOrganizationIgnored,
            };

            if (item.isOrganizationIgnored) {
              if (!organizationIgnoredRuleIds.has(item.ruleUuid)) {
                organizationIgnoredRules.push(item);
                organizationIgnoredRuleIds.add(item.ruleUuid);
              }

              return;
            }

            if (result.output && !errorIds.has(item.ruleUuid)) {
              // Drop errors hidden by a review (same rule + contract document +
              // validation version), mirroring the count helper's matching.
              const isHidden = hiddenDocumentValidationReviews?.some(
                (review) =>
                  review.ruleUuid === item.ruleUuid &&
                  review.contractDocumentUuid === item.contractDocumentUuid &&
                  review.ingestionDocumentValidation?.id ===
                    item.ingestionDocumentValidationId &&
                  review.isHidden
              );

              if (!isHidden) {
                errors.push(item);

                if (item.ruleUuid) {
                  errorIds.add(item.ruleUuid);
                }
              }
            }

            if (!result.output && !successIds.has(item.ruleUuid)) {
              successes.push(item);

              if (item.ruleUuid) {
                successIds.add(item.ruleUuid);
              }
            }
          }
        });
      }
    }
  );

  const fieldPositionLookup = buildFieldPositionLookup(document);

  return {
    errors: sortValidationItemsByPage(errors, fieldPositionLookup),
    successes: sortValidationItemsByPage(successes, fieldPositionLookup),
    organizationIgnoredRules: sortValidationItemsByPage(
      organizationIgnoredRules,
      fieldPositionLookup
    ),
  };
}

/**
 * Gets error and success counts for a specific contract document.
 *
 * This function filters out hidden errors based on the following matching rules:
 * - A review hides an error when ALL of these conditions are met:
 *   1. review.ruleUuid matches the error's ruleUuid
 *   2. review.contractDocumentUuid equals the provided contractDocumentUuid
 *   3. review.ingestionDocumentValidation.id equals the current validation's id (validation-version-specific hide)
 *   4. review.isHidden is true
 *
 * @param contractDocumentUuid - The UUID of the contract document to count errors for
 * @param validationData - The validation data containing document validations and results
 * @param hiddenDocumentValidationReviews - Optional array of review records marking errors as hidden
 * @returns Object containing counts of { errors, successes }
 */
function getErrorAndSuccessCountsForContractDocument(
  contractDocumentUuid: string,
  validationData: ComplianceValidationReportData,
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[]
): { errors: number; successes: number } {
  if (!validationData?.documentValidations) {
    return { errors: 0, successes: 0 };
  }

  let errorCount = 0;
  let successCount = 0;

  validationData.documentValidations.forEach(
    (docValidation: ComplianceIngestionDocumentValidation) => {
      const aiValidationResults =
        docValidation.aiValidationResults as unknown as
          | ValidationResult[]
          | undefined;
      const validationResults = docValidation.validationResults as unknown as
        | ValidationResult[]
        | undefined;

      const resultsToProcess =
        aiValidationResults && aiValidationResults.length > 0
          ? aiValidationResults
          : validationResults;

      if (resultsToProcess) {
        resultsToProcess.forEach((result) => {
          if (result.contractDocumentUuid === contractDocumentUuid) {
            if (result.isOrganizationIgnored) {
              return;
            }

            if (result.output) {
              // Check if this error is hidden based on matching ruleUuid, contractDocumentUuid, and validation ID
              const isHidden = hiddenDocumentValidationReviews?.some(
                (review) =>
                  review.ruleUuid === result.ruleUuid &&
                  review.contractDocumentUuid === contractDocumentUuid &&
                  review.ingestionDocumentValidation?.id === docValidation.id &&
                  review.isHidden
              );

              // Only count if not hidden
              if (!isHidden) {
                errorCount++;
              }
            } else {
              successCount++;
            }
          }
        });
      }
    }
  );

  return { errors: errorCount, successes: successCount };
}

/**
 * Gets error and success counts for an entire ingestion document (all its contract documents)
 */
export function getErrorAndSuccessCountsForIngestionDocument(
  document: ExternalIngestionDocumentFragment,
  validationData: ComplianceValidationReportData,
  contractDocumentId?: string,
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[]
): { errors: number; successes: number } {
  if (!document.contractDocuments || !validationData?.documentValidations) {
    return { errors: 0, successes: 0 };
  }

  // If contractDocumentId is provided, only count for that specific contract document
  if (contractDocumentId) {
    return getErrorAndSuccessCountsForContractDocument(
      contractDocumentId,
      validationData,
      hiddenDocumentValidationReviews
    );
  }

  let totalErrorCount = 0;
  let totalSuccessCount = 0;

  document.contractDocuments.forEach((contractDoc) => {
    const counts = getErrorAndSuccessCountsForContractDocument(
      contractDoc.id,
      validationData,
      hiddenDocumentValidationReviews
    );
    totalErrorCount += counts.errors;
    totalSuccessCount += counts.successes;
  });

  return { errors: totalErrorCount, successes: totalSuccessCount };
}

/**
 * Converts a field key into a human-readable label.
 *
 * Transforms nested field keys by replacing structural characters with readable separators
 * and applying title case formatting.
 *
 * @param key - The field key to transform (e.g., "buyer[0].name_first")
 * @returns A human-readable label (e.g., "Buyer > 0 Name | First")
 *
 * @example
 * friendlifyLabel("buyer[0].name_first") // "Buyer > 0 Name | First"
 * friendlifyLabel("seller.address_street") // "Seller | Address Street"
 */
const friendlifyLabel = (key: string) => {
  const replacedKey = key
    .replaceAll(']', ' ')
    .replaceAll('_', ' ')
    .replaceAll('.', ' | ')
    .replaceAll('[', ' > ')
    .replaceAll('  ', ' ');

  return titleCase(replacedKey, {
    titleTerminators: new Set([...TITLE_TERMINATORS, '|', '>']),
  });
};

/**
 * Groups and formats input values for display in tooltips
 */

/**
 * Strips -repositioned suffix from a fieldUuid if present
 */
function cleanFieldUuid(fieldUuid: string): string {
  return fieldUuid.replace(/-repositioned$/, '');
}

/**
 * Type guard to check if an unknown value is a MultiFieldValue
 */
function isMultiFieldValue(value: unknown): value is MultiFieldValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'fieldUuid' in value &&
    'value' in value &&
    typeof value.fieldUuid === 'string' &&
    typeof value.value === 'string'
  );
}

/**
 * Type guard to check if an unknown value is a MultiFieldValue array
 */
function isMultiFieldValueArray(value: unknown): value is MultiFieldValue[] {
  return (
    Array.isArray(value) && value.length > 0 && isMultiFieldValue(value[0])
  );
}

/**
 * Extracts field UUIDs from a MultiFieldValue array value
 * Returns array of UUIDs if the value is a valid MultiFieldValue array, null otherwise
 */
export function extractFieldUuidsFromValue(value: unknown): string[] | null {
  try {
    let parsed: unknown;

    // If value is already an object/array, use it directly
    if (typeof value === 'object' && value !== null) {
      parsed = value;
    } else if (typeof value === 'string') {
      parsed = JSON.parse(value);
    } else {
      return null;
    }

    if (isMultiFieldValueArray(parsed)) {
      // Strip -repositioned suffix from field UUIDs as a safety measure
      return parsed.map((item) => cleanFieldUuid(item.fieldUuid));
    }
  } catch (e) {
    // Not JSON or not a MultiFieldValue array
  }

  return null;
}

/**
 * Parses a value that might be a JSON-serialized MultiFieldValue array
 * Returns formatted string showing each field's status
 */
function parseMultiFieldValue(value: string): string | null {
  try {
    const parsed: unknown = JSON.parse(value);

    if (isMultiFieldValueArray(parsed)) {
      // If only one field, just show status without number
      // If multiple fields, show number and status (e.g., "1: Signed, 2: Unsigned")
      if (parsed.length === 1) {
        const status = parsed[0].value ? 'Signed' : 'Unsigned';

        return status;
      }

      return parsed
        .map((item, index) => {
          const status = item.value ? 'Signed' : 'Unsigned';

          return `${index + 1}: ${status}`;
        })
        .join(', ');
    }
  } catch (e) {
    // Not JSON or not a MultiFieldValue array, return null
  }

  return null;
}

export function formatInputValuesForTooltip(
  inputValues?: {
    variableSource?: VariableSource;
    fieldKey: string;
    value: string;
  }[],
  document?: GetIngestionDocumentFragment | null,
  variant: 'internal' | 'external' = 'external'
): {
  fromContract: Array<{ label: string; value: string }>;
  externalReference: Array<{ label: string; value: string }>;
} {
  if (!inputValues || inputValues.length === 0) {
    return { fromContract: [], externalReference: [] };
  }

  const fieldKeyToLabel = createFieldKeyToLabelMapping(document);

  const fromContract: Array<{ label: string; value: string }> = [];
  const externalReference: Array<{ label: string; value: string }> = [];

  const shouldShowInputValue = (fieldKey: string) => {
    if (fieldKey === 'ingestion.documentFormNames') {
      return variant === 'internal';
    }

    return true;
  };

  inputValues.forEach((input) => {
    if (!shouldShowInputValue(input.fieldKey)) {
      return;
    }
    const friendlyLabel =
      fieldKeyToLabel[input.fieldKey] || friendlifyLabel(input.fieldKey);

    // Value is guaranteed to be a string from getValidationResultsForDocument
    const stringValue = input.value;

    // Try to parse as MultiFieldValue array and format it for display
    const multiFieldDisplay = parseMultiFieldValue(stringValue);

    const friendlyValue =
      multiFieldDisplay ||
      (!stringValue || stringValue === 'undefined' ? '-' : stringValue);

    const displayItem = { label: friendlyLabel, value: friendlyValue };

    const source = input.variableSource ?? VariableSource.FIELD;

    if (source === VariableSource.FIELD) {
      if (fieldKeyToLabel[input.fieldKey]) {
        fromContract.push(displayItem);
      }
    } else {
      // All other sources are considered external reference data
      externalReference.push(displayItem);
    }
  });

  return { fromContract, externalReference };
}

export interface ImprovedField {
  fieldKey: string;
  fieldLabel: string;
  reductionCount: number;
}

export interface AnalysisWithFields {
  analysis: string;
  contractDocumentUuid: string;
  relatedFieldKeys?: string[];
}

export interface ValidationComparisonData {
  aiErrorCount: number;
  regularErrorCount: number;
  aiWarningCount: number;
  regularWarningCount: number;
  reductionPercentage: number;
  hasAiData: boolean;
  latestValidationDate?: string;
  aiAnalysisData?: string[];
  improvedFieldsList?: ImprovedField[];
  aiAnalysisWithFields?: AnalysisWithFields[];
}

/**
 * Calculates percentage reduction in validation issues
 */
function calculateReductionPercentage(
  totalRegularIssues: number,
  totalAiIssues: number
): number {
  if (totalRegularIssues === 0) return 0;

  return Math.round(
    ((totalRegularIssues - totalAiIssues) / totalRegularIssues) * 100
  );
}

/**
 * Extracts fields that were improved by AI processing
 */
function extractImprovedFields(
  fieldComparisons: Array<{
    fieldKey: string;
    fieldLabel?: string;
    changeType: string;
    reductionCount: number;
  }>
): ImprovedField[] {
  return fieldComparisons
    .filter(
      (field) =>
        field.changeType === 'removed' ||
        (field.changeType === 'modified' && field.reductionCount > 0)
    )
    .map((field) => ({
      fieldKey: field.fieldKey,
      fieldLabel: field.fieldLabel || field.fieldKey,
      reductionCount: field.reductionCount,
    }));
}

/**
 * Associates AI analysis insights with related field keys
 */
function buildAnalysisWithFields(
  document: GetIngestionDocumentFragment,
  validationForDocument: ComplianceIngestionDocumentValidation,
  fieldComparisons: Array<{
    fieldKey: string;
    changeType: string;
    reductionCount: number;
    aiResults: ValidationResult[];
  }>
): AnalysisWithFields[] {
  const analysisWithFields: AnalysisWithFields[] = [];

  const analyses = validationForDocument.aiAnalysis as
    | Array<{
        contractDocumentUuid: string;
        analysis: string;
      }>
    | undefined;

  if (!analyses) return [];

  analyses.forEach((analysisItem) => {
    const relatedFieldKeys = fieldComparisons
      .filter(
        (field) =>
          (field.changeType === 'removed' ||
            (field.changeType === 'modified' && field.reductionCount > 0)) &&
          field.aiResults.some(
            (result) =>
              result.contractDocumentUuid === analysisItem.contractDocumentUuid
          )
      )
      .map((field) => field.fieldKey);

    analysisWithFields.push({
      analysis: analysisItem.analysis,
      contractDocumentUuid: analysisItem.contractDocumentUuid,
      relatedFieldKeys:
        relatedFieldKeys.length > 0 ? relatedFieldKeys : undefined,
    });
  });

  return analysisWithFields;
}

/**
 * Gets the validation for a specific IngestionDocument.
 */
export function getValidationForDocument(
  document: GetIngestionDocumentFragment,
  validationData?: ComplianceValidationReportData
): ComplianceIngestionDocumentValidation | null {
  if (!validationData?.documentValidations) {
    return null;
  }

  const validations = validationData.documentValidations.filter(
    (validation) => document.id === validation.ingestionDocument.id
  );

  if (validations.length === 0) {
    return null;
  }

  if (validations.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(`Multiple validations found for document ${document.id}.`);
  }

  return validations[0];
}

/**
 * Compares AI vs regular validation results for a specific document
 */
export function getValidationComparisonData(
  document: GetIngestionDocumentFragment,
  validationForDocument: ComplianceIngestionDocumentValidation
): ValidationComparisonData | null {
  const comparisonData: ValidationComparisonData = {
    aiErrorCount: 0,
    aiWarningCount: 0,
    regularErrorCount: 0,
    regularWarningCount: 0,
    reductionPercentage: 0,
    hasAiData: false,
  };

  let hasAiProcessing = false;
  let mostRecentValidationDate: string | undefined;

  if (!!validationForDocument.aiValidationResults?.length) {
    hasAiProcessing = true;
  }

  if (validationForDocument.updatedAt) {
    mostRecentValidationDate = validationForDocument.updatedAt as string;
  }

  comparisonData.aiErrorCount = validationForDocument.aiErrorCount;
  comparisonData.aiWarningCount = validationForDocument.aiWarningCount;
  comparisonData.regularErrorCount = validationForDocument.errorCount;
  comparisonData.regularWarningCount = validationForDocument.warningCount;

  const totalRegularIssues =
    comparisonData.regularErrorCount + comparisonData.regularWarningCount;
  const totalAiIssues =
    comparisonData.aiErrorCount + comparisonData.aiWarningCount;
  const improvementPercentage = calculateReductionPercentage(
    totalRegularIssues,
    totalAiIssues
  );

  const fieldComparisonData = getFieldLevelComparisonData(
    document,
    validationForDocument
  );
  const improvedFields = extractImprovedFields(
    fieldComparisonData.fieldComparisons
  );
  const analysisWithFieldContext = buildAnalysisWithFields(
    document,
    validationForDocument,
    fieldComparisonData.fieldComparisons
  );

  return {
    ...comparisonData,
    reductionPercentage: improvementPercentage,
    hasAiData: hasAiProcessing,
    latestValidationDate: mostRecentValidationDate,
    improvedFieldsList: improvedFields.length > 0 ? improvedFields : undefined,
    aiAnalysisWithFields:
      analysisWithFieldContext.length > 0
        ? analysisWithFieldContext
        : undefined,
  };
}

/**
 * Gets validation timestamps from document validations
 */
export function getValidationTimestamps(
  validationForDocument: ComplianceIngestionDocumentValidation
): {
  createdAt?: string;
  updatedAt?: string;
  isUpToDate: boolean;
} {
  if (!validationForDocument) {
    return { isUpToDate: false };
  }

  // TODO: BLO-2146: use validatedAt and aiValidationUpdatedAt
  // Get the most recent validation
  const mostRecent = validationForDocument.updatedAt as string;

  if (!mostRecent) {
    return { isUpToDate: false };
  }

  const updatedAt = mostRecent;
  const createdAt = validationForDocument.createdAt as string;

  // Consider validation up to date if it's less than 24 hours old
  const isUpToDate = updatedAt
    ? new Date().getTime() - new Date(updatedAt).getTime() < 24 * 60 * 60 * 1000
    : false;

  return {
    createdAt,
    updatedAt,
    isUpToDate,
  };
}

export function hasDocumentBeenValidated(
  document: ExternalIngestionDocumentFragment,
  validationData: ComplianceValidationReportData
): boolean {
  if (!validationData?.documentValidations) {
    return false;
  }

  return validationData.documentValidations.some(
    (docValidation: ComplianceIngestionDocumentValidation) => {
      const aiResults = docValidation.aiValidationResults as unknown as
        | { ingestionDocumentUuid?: string }[]
        | undefined;
      const regularResults = docValidation.validationResults as unknown as
        | { ingestionDocumentUuid?: string }[]
        | undefined;
      const results = aiResults?.length ? aiResults : regularResults;

      return results?.some(
        (result) => result.ingestionDocumentUuid === document.id
      );
    }
  );
}

export function hasIngestionDocumentBeenClassified(
  document: ExternalIngestionDocumentFragment
): boolean {
  return (document.contractDocuments?.length || 0) > 0;
}

/**
 * Gets display name for a contract document
 *
 * @param contractDoc - The contract document to get display name for
 * @returns Formatted display name like "Form - Name" or fallback to category
 */
export function getContractDocDisplayName(contractDoc?: {
  documentCategory?: string;
  documentVersion?: {
    document?: {
      form?: string;
      name?: string;
    };
  };
}): string {
  const docForm = contractDoc?.documentVersion?.document?.form;
  const docName = contractDoc?.documentVersion?.document?.name;
  const fallbackName = contractDoc?.documentCategory;

  if (docForm && docName) {
    return `${docForm} - ${docName}`;
  }

  return fallbackName || 'Unknown Document';
}
